import { useState } from 'react';
import Button from './ui/Button';
import { submitFormPayload } from '../utils/submitForm';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  horizon: '',
  risk: '',
  tax: '',
  question: '',
  comments: ''
};

export default function IntakeForm() {
  const [form, setForm] = useState(initialState);
  const [status, setStatus] = useState(null);

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.phone.trim()) {
      setStatus({ type: 'error', message: 'Please complete the required fields so we can follow up.' });
      return;
    }
    if (!emailPattern.test(form.email)) {
      setStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }

    setStatus({ type: 'loading', message: 'Submitting your request…' });

    const response = await submitFormPayload({
      formName: 'Home – No-Cost Consultation',
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone,
      timeHorizon: form.horizon,
      riskComfort: form.risk,
      taxBracketEstimate: form.tax,
      question: form.question,
      comments: form.comments
    });

    if (response?.success) {
      setStatus({ type: 'success', message: 'Thank you! A member of our team will reach out within one business day.' });
      setForm(initialState);
    } else {
      console.error('Form submit failed', response);
      setStatus({ type: 'error', message: 'Something went wrong submitting your request. Please try again in a moment.' });
    }
  }

  const emailValid = emailPattern.test(form.email);
  const canSubmit = form.firstName.trim() && form.lastName.trim() && emailValid && form.phone.trim();

  return (
    <form onSubmit={handleSubmit} className="space-y-5" id="no-cost-consultation-form" noValidate>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-slate-700">
            First Name *
          </label>
          <input
            id="firstName"
            name="firstName"
            required
            value={form.firstName}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border-slate-300 px-3 py-3 md:py-2 text-base md:text-sm focus:ring-cam-blue focus:border-cam-blue"
          />
        </div>
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-slate-700">
            Last Name *
          </label>
          <input
            id="lastName"
            name="lastName"
            required
            value={form.lastName}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border-slate-300 px-3 py-3 md:py-2 text-base md:text-sm focus:ring-cam-blue focus:border-cam-blue"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700">
            Email *
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            value={form.email}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border-slate-300 px-3 py-3 md:py-2 text-base md:text-sm focus:ring-cam-blue focus:border-cam-blue"
          />
        </div>
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-slate-700">
            Phone Number *
          </label>
          <input
            id="phone"
            name="phone"
            required
            value={form.phone}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border-slate-300 px-3 py-3 md:py-2 text-base md:text-sm focus:ring-cam-blue focus:border-cam-blue"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="horizon" className="block text-sm font-medium text-slate-700">
            Time Horizon
          </label>
          <select
            id="horizon"
            name="horizon"
            value={form.horizon}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border-slate-300 px-3 py-3 md:py-2 text-base md:text-sm focus:ring-cam-blue focus:border-cam-blue"
          >
            <option value="">Select…</option>
            <option>0–3 years</option>
            <option>3–7 years</option>
            <option>7+ years</option>
          </select>
        </div>
        <div>
          <label htmlFor="risk" className="block text-sm font-medium text-slate-700">
            Investment Risk Comfort Level
          </label>
          <select
            id="risk"
            name="risk"
            value={form.risk}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border-slate-300 px-3 py-3 md:py-2 text-base md:text-sm focus:ring-cam-blue focus:border-cam-blue"
          >
            <option value="">Select…</option>
            <option>Conservative</option>
            <option>Balanced</option>
            <option>Growth</option>
          </select>
        </div>
        <div>
          <label htmlFor="tax" className="block text-sm font-medium text-slate-700">
            Estimated Tax Bracket
          </label>
          <select
            id="tax"
            name="tax"
            value={form.tax}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border-slate-300 px-3 py-3 md:py-2 text-base md:text-sm focus:ring-cam-blue focus:border-cam-blue"
          >
            <option value="">Select…</option>
            <option>10%</option>
            <option>12%</option>
            <option>22%</option>
            <option>24%</option>
            <option>32%</option>
            <option>35%</option>
            <option>37%</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="question" className="block text-sm font-medium text-slate-700">
          One question you would like answered
        </label>
        <textarea
          id="question"
          name="question"
          rows="3"
          value={form.question}
          onChange={handleChange}
            className="mt-1 w-full rounded-lg border-slate-300 px-3 py-3 md:py-2 text-base md:text-sm focus:ring-cam-blue focus:border-cam-blue"
        />
      </div>
      <div>
        <label htmlFor="comments" className="block text-sm font-medium text-slate-700">
          Any other comments, questions, or concerns
        </label>
        <textarea
          id="comments"
          name="comments"
          rows="3"
          value={form.comments}
          onChange={handleChange}
          className="mt-1 w-full rounded-lg border-slate-300 focus:ring-cam-blue focus:border-cam-blue"
        />
      </div>

      <div className="rounded-2xl bg-cam-blue/5 p-4 text-sm text-slate-600">
        Investing involves risk, including loss of principal. No strategy ensures success or protects against loss. Past performance is not indicative of future results. Responses are informational and do not create an advisory relationship.
      </div>
      <Button
        type="submit"
        className="w-full focus:ring-offset-white sm:w-auto"
        disabled={!canSubmit || status?.type === 'loading'}
      >
        {status?.type === 'loading' ? 'Submitting…' : 'Schedule a Consultation'}
      </Button>
      {status && (
        <p className={`text-sm ${status.type === 'error' ? 'text-red-600' : 'text-cam-blue'}`} role="status">
          {status.message}
        </p>
      )}
    </form>
  );
}
